import { useState } from 'react';
import { Link } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { fmtDate } from '../lib/format';
import type { DuplicatePair, DuplicateSide } from '../lib/types';
import { Button, Callout, Checkbox, Empty, ErrorState, Ltr, SkeletonRows, useConfirm, useToast } from '../components/ui';
import { IconMerge } from '../components/icons';

const CONF = { high: { label: 'סבירות גבוהה', tone: 'bad' }, medium: { label: 'סבירות בינונית', tone: 'warn' }, low: { label: 'סבירות נמוכה', tone: '' } } as const;

export function DuplicatesPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const { data, isLoading, error, refetch } = useQuery({ queryKey: ['duplicates'], queryFn: () => api<{ items: DuplicatePair[] }>('/api/duplicates') });
  const [showLow, setShowLow] = useState(false);
  const items = (data?.items ?? []).filter((p) => showLow || p.confidence !== 'low');
  const lowCount = (data?.items ?? []).filter((p) => p.confidence === 'low').length;

  return (
    <>
      <div className="page-head">
        <div>
          <div className="crumbs"><Link to="/customers">לקוחות</Link></div>
          <h1>לקוחות כפולים</h1>
          <div className="sub">הצעות לאיחוד — שום דבר לא מאוחד בלי אישור מנהל. לפני כל איחוד נשמר גיבוי.</div>
        </div>
        {lowCount ? <Checkbox checked={showLow} onChange={setShowLow}>הצגת התאמות בסבירות נמוכה ({lowCount})</Checkbox> : null}
      </div>
      {!isAdmin ? <Callout tone="warn">איחוד לקוחות מותר למנהל בלבד. אפשר לסמן "לא כפול".</Callout> : null}
      {error ? (
        <div className="panel"><ErrorState error={error} onRetry={() => refetch()} /></div>
      ) : isLoading ? (
        <div className="panel"><table className="table"><tbody><SkeletonRows rows={4} cols={3} /></tbody></table></div>
      ) : items.length === 0 ? (
        <div className="panel"><Empty title="לא נמצאו לקוחות כפולים" body="המערכת בודקת ח.פ זהה, שמות זהים או דומים, שמות שנלמדו מהמסמכים וקבלות שמתייחסות לחשבונית של לקוח אחר." /></div>
      ) : (
        <div className="stack">
          {items.map((p) => <PairCard key={`${p.keep.id}-${p.merge.id}`} pair={p} canMerge={isAdmin} />)}
        </div>
      )}
    </>
  );
}

function PairCard({ pair, canMerge }: { pair: DuplicatePair; canMerge: boolean }) {
  // The user can swap which record survives.
  const [flipped, setFlipped] = useState(false);
  const keep = flipped ? pair.merge : pair.keep;
  const merge = flipped ? pair.keep : pair.merge;
  const [busy, setBusy] = useState<'merge' | 'dismiss' | null>(null);
  const qc = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();
  const refresh = () => {
    for (const k of ['duplicates', 'customers', 'customer', 'customer-options', 'dashboard', 'workplan', 'followups']) qc.invalidateQueries({ queryKey: [k] });
  };

  const doMerge = async () => {
    const ok = await confirm({
      title: `לאחד את "${merge.businessName}" לתוך "${keep.businessName}"?`,
      body: (
        <>
          {merge.documents} מסמכים, כתובות, שמות ופעילות יעברו ל"{keep.businessName}". "{merge.businessName}" יימחק והשם שלו יישמר כשם נוסף, כך שמסמכים עתידיים יזוהו.
          {pair.cnConflict ? <><br /><strong>שימו לב: לשני הלקוחות ח.פ שונה.</strong></> : null}
          <br />נשמר גיבוי לפני האיחוד.
        </>
      ),
      confirmLabel: 'איחוד',
      danger: true,
    });
    if (!ok) return;
    setBusy('merge');
    try {
      const r = await api<{ documentsMoved: number }>('/api/duplicates/merge', { body: { keepId: keep.id, mergeId: merge.id } });
      toast.success(`אוחד — ${r.documentsMoved} מסמכים הועברו`);
      refresh();
    } catch (e) {
      toast.error(e);
    } finally {
      setBusy(null);
    }
  };
  const dismiss = async () => {
    setBusy('dismiss');
    try {
      await api('/api/duplicates/dismiss', { body: { a: keep.id, b: merge.id } });
      toast.success('סומן כלא כפול');
      refresh();
    } catch (e) {
      toast.error(e);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="panel">
      <div className="panel-head">
        <span className={`badge ${CONF[pair.confidence].tone}`}>{CONF[pair.confidence].label}</span>
        <span className="small muted">{pair.reasons.join(' · ')}</span>
        {pair.cnConflict ? <span className="badge warn">ח.פ שונה</span> : null}
      </div>
      <div className="dup-pair">
        <Side s={keep} role="keep" />
        <div className="row" style={{ flexDirection: 'column', justifyContent: 'center', gap: 6 }}>
          <IconMerge size={18} />
          <Button size="sm" variant="ghost" onClick={() => setFlipped(!flipped)} title="להחליף איזה לקוח נשאר">⇄</Button>
        </div>
        <Side s={merge} role="merge" />
      </div>
      <div className="panel-foot row wrap">
        <Button variant="primary" size="sm" onClick={doMerge} loading={busy === 'merge'} disabled={!canMerge || busy !== null}>
          איחוד לתוך "{keep.businessName}"
        </Button>
        <Button size="sm" onClick={dismiss} loading={busy === 'dismiss'} disabled={busy !== null}>לא כפול</Button>
      </div>
    </div>
  );
}

function Side({ s, role }: { s: DuplicateSide; role: 'keep' | 'merge' }) {
  return (
    <div className={`dup-side ${role}`}>
      <span className="small muted">{role === 'keep' ? 'נשאר' : 'יאוחד ויימחק'}</span>
      <Link to={`/customers/${s.id}`} className="strong" style={{ color: 'var(--ink)' }}>{s.businessName}</Link>
      {s.parentName ? <span className="small muted">סניף של {s.parentName}</span> : null}
      <span>ח.פ {s.companyNumber ? <Ltr>{s.companyNumber}</Ltr> : <span className="faint">—</span>}</span>
      {s.phone || s.email ? <span className="small">{s.phone ? <Ltr>{s.phone}</Ltr> : null} {s.email ? <bdi>{s.email}</bdi> : null}</span> : null}
      <span className="small">{s.documents} מסמכים{s.lastDocument ? ` · אחרון ${fmtDate(s.lastDocument)}` : ''}{s.branches ? ` · ${s.branches} סניפים` : ''}</span>
    </div>
  );
}
